import { Shield, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";

const TermsOfService = () => {
  useSEO({
    title: "Terms of Service | Global Conflict Monitor",
    description: "The Terms of Service governing use of the Global Conflict Monitor OSINT intelligence platform, including disclaimers, subscriptions, and acceptable use.",
    path: "/terms",
    ogType: "article",
  });
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="container mx-auto max-w-3xl px-4 py-12">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>

        <div className="mb-8 flex items-center gap-3">
          <Shield className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Terms of Service</h1>
        </div>

        <p className="mb-6 text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>

        <div className="space-y-8 text-sm leading-relaxed text-muted-foreground">
          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">1. Acceptance of Terms</h2>
            <p>By accessing and using Global Conflict Monitor ("the Platform"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Platform.</p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">2. Description of Service</h2>
            <p>Global Conflict Monitor is an OSINT (Open Source Intelligence) aggregation platform that provides real-time conflict monitoring data sourced from publicly available information and major news wires. The Platform is a decision-support tool and does not replace official government or civil protection communications.</p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">3. User Accounts</h2>
            <ul className="ml-4 list-disc space-y-1">
              <li>You must provide accurate and complete registration information.</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
              <li>You are responsible for all activities that occur under your account.</li>
              <li>You must notify us immediately of any unauthorized use of your account.</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">4. Premium Subscription</h2>
            <ul className="ml-4 list-disc space-y-1">
              <li>Premium features are available through a paid subscription at €2.99/month.</li>
              <li>Payments are processed securely via Stripe.</li>
              <li>You may cancel your subscription at any time. Access continues until the end of the current billing period.</li>
              <li>No refunds are provided for partial billing periods.</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">5. Acceptable Use</h2>
            <p className="mb-2">You agree not to:</p>
            <ul className="ml-4 list-disc space-y-1">
              <li>Use the Platform for any illegal or unauthorized purpose.</li>
              <li>Attempt to gain unauthorized access to any part of the Platform.</li>
              <li>Reproduce, distribute, or commercially exploit Platform content without written permission.</li>
              <li>Interfere with or disrupt the Platform's infrastructure.</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">6. Disclaimer of Warranties</h2>
            <p>The Platform is provided "as is" and "as available" without warranties of any kind. We do not guarantee the accuracy, completeness, or timeliness of any information displayed. In emergency situations, always follow instructions from local authorities and official channels.</p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">7. Limitation of Liability</h2>
            <p>Global Conflict Monitor shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Platform. Our total liability shall not exceed the amount you paid for the service in the 12 months preceding the claim.</p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">8. Changes to Terms</h2>
            <p>We reserve the right to modify these Terms at any time. We will notify registered users of significant changes via email. Continued use of the Platform after changes constitutes acceptance of the new Terms.</p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">9. Contact</h2>
            <p>For questions about these Terms, contact us at:</p>
            <p className="mt-2"><a href="mailto:support@globalconflictsmonitor.com" className="text-primary hover:underline">support@globalconflictsmonitor.com</a></p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
